/**
 * Open-book snapshot for Positions Overview.
 * Winners / losers ignore flat (exactly 0) P&L.
 */
export function buildPositionsOverviewStats(positions, getUnrealizedPL, missingStopCount = 0, metrics = {}) {
  const getCostBasis = metrics.getCostBasis;
  const getTodaysPL = metrics.getTodaysPL;
  const getMarketValue = metrics.getMarketValue;
  const rows = (positions || []).map((position) => {
    const pnl = Number(getUnrealizedPL?.(position));
    const cost = Number(getCostBasis?.(position));
    const today = Number(getTodaysPL?.(position));
    const value = Number(getMarketValue?.(position));
    const side = String(position?.LongShort || '').toLowerCase();
    return {
      symbol: String(position?.Symbol || '').toUpperCase(),
      pnl: Number.isFinite(pnl) ? pnl : 0,
      cost: Number.isFinite(cost) ? cost : 0,
      today: Number.isFinite(today) ? today : 0,
      value: Number.isFinite(value) ? value : 0,
      side,
    };
  });

  const winners = rows.filter((row) => row.pnl > 0);
  const losers = rows.filter((row) => row.pnl < 0);
  const totalPnl = rows.reduce((sum, row) => sum + row.pnl, 0);
  const totalCost = rows.reduce((sum, row) => sum + row.cost, 0);
  const todayPnl = rows.reduce((sum, row) => sum + row.today, 0);
  const totalValue = rows.reduce((sum, row) => sum + row.value, 0);
  const longRows = rows.filter((row) => row.side === 'long');
  const shortRows = rows.filter((row) => row.side === 'short');
  const accountEquity = Number(metrics.accountEquity);
  const withTakeProfitCount = Math.max(0, Number(metrics.withTakeProfitCount) || 0);
  const average = (list) => (
    list.length === 0
      ? null
      : list.reduce((sum, row) => sum + row.pnl, 0) / list.length
  );

  return {
    count: rows.length,
    totalPnl,
    totalPnlPercent: totalCost > 0 ? (totalPnl / totalCost) * 100 : null,
    todayPnl,
    todayPnlPercent: totalValue > 0 ? (todayPnl / totalValue) * 100 : null,
    winnerCount: winners.length,
    loserCount: losers.length,
    avgWinner: average(winners),
    avgLoser: average(losers),
    biggestWinner: winners.reduce((best, row) => (!best || row.pnl > best.pnl ? { symbol: row.symbol, pnl: row.pnl } : best), null),
    biggestLoser: losers.reduce((worst, row) => (!worst || row.pnl < worst.pnl ? { symbol: row.symbol, pnl: row.pnl } : worst), null),
    missingStopCount: Math.max(0, Number(missingStopCount) || 0),
    totalValue,
    exposurePercent: Number.isFinite(accountEquity) && accountEquity > 0
      ? (totalValue / accountEquity) * 100
      : null,
    winRate: (winners.length + losers.length) > 0
      ? (winners.length / (winners.length + losers.length)) * 100
      : null,
    avgPnl: rows.length > 0 ? totalPnl / rows.length : null,
    longCount: longRows.length,
    shortCount: shortRows.length,
    longPnl: longRows.reduce((sum, row) => sum + row.pnl, 0),
    shortPnl: shortRows.reduce((sum, row) => sum + row.pnl, 0),
    flatCount: rows.length - winners.length - losers.length,
    protectedCount: Math.max(0, rows.length - (Math.max(0, Number(missingStopCount) || 0))),
    withTakeProfitCount,
    largestPosition: rows.reduce((best, row) => (
      !best || row.value > best.value ? { symbol: row.symbol, value: row.value } : best
    ), null),
  };
}
